<template>
    <Dialog
        v-model:visible="visible"
        :header="header"
        :modal="modal"
        :dismissableMask="false"
        :style="style"
        :draggable="draggable"
        @show="callbackOnShow"
        @afterHide="callbackOnHide"
        class="adapters_dialog__container"
        :appendTo="appendTo"
        :block-scroll="true"
        ref="dialog"
        :pt="mergedPt"
    >
        <!-- header -->
        <template #header>
            <slot name="header"></slot>
        </template>
        <!-- loader -->
        <AdaptersLoader :loading="isLoading" />
        <!-- error -->
        <AdaptersMessage :message="errorMessage" />
        <!-- content -->
        <slot name="content"></slot>
        <!-- footer -->
        <template v-if="!disableFooter" #footer>
            <slot name="footer" v-bind:onClose="onClose">
                <div class="flex justify-content-between align-items-center pt-1 w-full flex-column sm:flex-row gap-3">
                    <div v-if="callbackAdditional || $slots.additional" class="pb-0 w-full sm:w-auto">
                        <slot name="additional">
                            <Button
                                :label="buttonAdditionalLabel"
                                @click="callbackAdditional"
                                severity="secondary"
                                class="w-full"
                            />
                        </slot>
                    </div>
                    <div class="flex pb-0 justify-content-end w-full">
                        <Button
                            v-if="callbackAgree"
                            :label="getButtonAgreeLabel"
                            @click="callbackAgree"
                            :disabled="isDisableSend"
                            data-pc-section="button-agree"
                            v-bind="getPT('button-agree')"
                        />
                        <Button :label="getButtonCancelLabel" @click="onClose" class="p-button-text" />
                    </div>
                </div>
            </slot>
        </template>
    </Dialog>
</template>

<script>
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import AdaptersMessage from "./Message.vue";
import AdaptersLoader from "./Loader.vue";
import Alerts from "../../mixins/Alerts";
import {UniqueComponentId} from 'primevue/utils';
import PassThrough from "../../mixins/PassThrough";

/**
 * Адаптер для компонента Dialog
 * @link https://primevue.org/dialog
 * Функционал параметра `dismissableMask` базового компонента переназначен по причине закрытия при mouseup на .p-dialog-mask
 */
export default {
    name: "AdaptersDialog",
    mixins: [Alerts, PassThrough],
    data() {
        return {
            visible: false,
            id: UniqueComponentId(),
            styleElement: null,
            mouseDownOnMask: false
        };
    },
    props: {
        // ID слушателя события отображения
        listenerId: {
            type: String,
            required: false,
        },
        // ID слушателя события закрытия
        listenerCloseId: {
            type: String,
            required: false,
        },
        // запрет блока управления с кнопками и слота footer
        disableFooter: {
            type: Boolean,
            required: false,
            default: false
        },
        // callback срабатывает при открытии диалога
        callbackOnShow: {
            type: Function,
            required: false,
            default: null
        },
        // callback срабатывает при закрытии
        callbackOnHide: {
            type: Function,
            required: false,
            default: null
        },
        // текст кнопки согласия
        buttonAgreeLabel: {
            type: String,
            required: false,
            default: null,
        },
        // callback срабатывает при согласии
        callbackAgree: {
            type: Function,
            required: false,
        },
        // текст отмены действия
        buttonCancelLabel: {
            type: String,
            required: false,
            default: null,
        },
        // текст "дополнительной" (вспомогающей) кнопки
        buttonAdditionalLabel: {
            type: String,
            required: false,
            default: null,
        },
        // callback срабатывает при нажатии "дополнительной" (вспомогающей) кнопки
        callbackAdditional: {
            type: Function,
            required: false,
        },
        // стандартный вывод (оформление) заголовка диалога
        header: {
            type: String,
            required: false,
            default: null,
        },
        // отключение скролла фоновой страницы
        modal: {
            type: Boolean,
            required: false,
            default: true,
        },
        dismissableMask: {
            type: Boolean,
            required: false,
            default: true,
        },
        breakpoints: {
            type: Object,
            required: false,
            default: {'8000px': '700px', '700px': '100vw'}
        },
        style: {
            type: Object,
            required: false,
            default: {'min-width': '50vw'}
        },
        draggable: {
            type: Boolean,
            required: false,
            default: false,
        },
        // Лоадер загрузки
        loading: {
            type: Boolean,
            required: false,
            default: false,
        },
        // Текст ошибки
        errorMessage: {
            type: String,
            required: false,
            default: null
        },
        // Запрет кнопки согласия
        isDisableSend: {
            type: Boolean,
            required: false,
            default: false,
        },
        appendTo: {
            type: String,
            required: false,
            default: '#keysoApp'
        }
    },
    mounted() {
        // слушатель отображения диалогового окна
        this.$emitter.on(this.listenerId, () => {
            this.onShow();
        });
        // слушатель закрытия диалогового окна
        if (this.listenerCloseId) {
            this.$emitter.on(this.listenerCloseId, () => {
                this.visible = false;
            });
        }
        /**
         * Фикс закрытия окна при mouseup на .p-dialog-mask
         * Пример: когда выделяешь внутри попап текст, но отпускаешь за его пределами.
         * Перехватываем событие зажатие и отпускания кнопки и проверяем чтобы они были оба на .p-dialog-mask
         */
        if (this.dismissableMask) {
            document.addEventListener('mousedown', this.onMouseDown, true);
            document.addEventListener('mouseup', this.onMouseUp, true);
        }
    },
    beforeUnmount() {
        document.removeEventListener('mousedown', this.onMouseDown, true);
        document.removeEventListener('mouseup', this.onMouseUp, true);
    },
    computed: {
        mergedPt() {
            return this.mergePT(
                {root: {'data-adapter-dialog-id': this.id}},
                this.pt,
            );
        },
        getButtonAgreeLabel() {
            return this.buttonAgreeLabel ?? this.$t('lang.modal.agree');
        },
        getButtonCancelLabel() {
            return this.buttonCancelLabel ?? this.$t('lang.modal.cancel');
        },
        isLoading() {
            return !!this.loading;
        },
    },
    methods: {
        onShow() {
            this.createStyleBreakpoints();
            this.visible = true;
        },
        onClose() {
            this.visible = false;
        },
        createStyleBreakpoints() {
            if (this.breakpoints && !this.styleElement) {
                this.styleElement = document.createElement('style');
                this.styleElement.type = 'text/css';

                let innerHTML = '';

                for (let breakpoint in this.breakpoints) {
                    innerHTML += `
                        @media screen and (max-width: ${breakpoint}) {
                            .p-dialog[data-adapter-dialog-id="${this.id}"] {
                                width: ${this.breakpoints[breakpoint]} !important;
                            }
                        }
                    `;
                }

                this.styleElement.innerHTML = innerHTML;
                document.head.appendChild(this.styleElement);
            }
        },
        getMaskElement() {
            return document.querySelector('.p-dialog-mask');
        },
        onMouseDown(event) {
            const mask = this.getMaskElement();
            this.mouseDownOnMask = mask && event.target === mask;
        },
        onMouseUp(event) {
            const mask = this.getMaskElement();
            const mouseUpOnMask = mask && event.target === mask;

            // Закрываем ТОЛЬКО если оба события были на mask
            if (this.mouseDownOnMask && mouseUpOnMask) {
                this.visible = false;
            }

            this.mouseDownOnMask = false;
        }
    },
    components: {
        Dialog,
        Button,
        AdaptersMessage,
        AdaptersLoader,
    }
}
</script>

<style scoped>
/* сдвиг заголовка для выравнивания закрывающего крестика по уровню контента */
::v-global(.adapters_dialog__container .p-dialog-header .p-dialog-header-icon:last-child) {
    margin-right: -0.55rem;
}
/* убираем выделение крестика из-за визуального сдвига */
::v-global(.adapters_dialog__container .p-dialog-header .p-dialog-header-icon:enabled:hover) {
    background-color: transparent;
}
::v-global(.adapters_dialog__container .p-dialog-header .p-dialog-header-icon:focus) {
    box-shadow: none;
}
::v-global(.adapters_dialog__container .p-dialog-header),
::v-global(.adapters_dialog__container .p-dialog-content),
::v-global(.adapters_dialog__container .p-dialog-footer) {
    padding-left: 1.3125rem;
    padding-right: 1.3125rem;
}
::v-global(.adapters_dialog__container .p-dialog-title) {
    font-size: 1.125rem;
    line-height: 1.2;
}
::v-global(.adapters_dialog__container .p-dialog-content) {
    padding-bottom: 1.75rem;
}
</style>
